import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { toast } from "react-toastify";
import {
  addSingleLearner,
  getAllGroups,
  getAllSemesters,
  getAllEnrolmentYears,
} from "../../../services/adminServices/CoeServices";
import { getAllCoeLabs } from "../../../services/mentorServices/DailyPracticeServices";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  maxHeight: "92vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3.5,
  borderRadius: "8px",
  outline: "none",
  overflowY: "auto",
};

const INITIAL_FORM_STATE = {
  name: "",
  email: "",
  institution_email: "",
  mobile: "",
  device_id: "",
  roll_num: "",
  passout_year: "",
  stream: "",
  enrolment_year: "",
  current_semester: "",
  lab_name: "",
  lab_id: "",
};

const DEFAULT_SEMESTERS = [1, 2, 3, 4, 5, 6, 7, 8];

const AddStudentModal = ({
  open,
  onClose,
  coeId,
  fetchData,
  fetchCoeData,
}) => {
  const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [isCustomGroup, setIsCustomGroup] = useState(false);
  const [labOptions, setLabOptions] = useState([]);
  const [labLoading, setLabLoading] = useState(false);
  const [semesterOptions, setSemesterOptions] = useState(DEFAULT_SEMESTERS);
  const [enrolmentYearOptions, setEnrolmentYearOptions] = useState([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await getAllGroups();
        setGroupOptions(response?.data?.data || []);
      } catch (err) {
        setGroupOptions([]);
      }

      if (coeId) {
        setLabLoading(true);
        try {
          const [semRes, yearRes, labRes] = await Promise.all([
            getAllSemesters(coeId).catch(() => ({ data: { data: [] } })),
            getAllEnrolmentYears(coeId).catch(() => ({ data: { data: [] } })),
            getAllCoeLabs(coeId).catch(() => ({ data: { data: [] } })),
          ]);

          const sems = semRes?.data?.data || [];
          const sortedSemesters = Array.isArray(sems) && sems.length > 0
            ? sems.map(Number).sort((a, b) => a - b)
            : DEFAULT_SEMESTERS;
          setSemesterOptions(sortedSemesters);

          const years = yearRes?.data?.data || [];
          const sortedYears = Array.isArray(years) && years.length > 0
            ? years.map(Number).sort((a, b) => b - a)
            : [];
          setEnrolmentYearOptions(sortedYears);

          const labs = labRes?.data?.data || [];
          setLabOptions(Array.isArray(labs) ? labs : []);
        } catch (err) {
          setSemesterOptions(DEFAULT_SEMESTERS);
        } finally {
          setLabLoading(false);
        }
      }
    };
    if (open) {
      fetchInitialData();
    }
  }, [open, coeId]);

  useEffect(() => {
    if (!open) {
      setFormValues(INITIAL_FORM_STATE);
      setErrors({});
      setSelectedGroup("");
      setIsCustomGroup(false);
      setLoading(false);
    }
  }, [open]);

  const fetchFilteredLabs = async (semester, year) => {
    if (!coeId) return;
    setLabLoading(true);
    try {
      const labRes = await getAllCoeLabs(coeId, "", semester || "", year || "");
      const labs = labRes?.data?.data || [];
      if (Array.isArray(labs) && labs.length > 0) {
        setLabOptions(labs);
      } else {
        setLabOptions([]);
        setFormValues((prev) => ({ ...prev, lab_name: "" }));
        toast.error(
          "No labs found for provided semester or enrolment year. Change the semester or enrolment year."
        );
      }
    } catch (err) {
      setLabOptions([]);
      setFormValues((prev) => ({ ...prev, lab_name: "" }));
      toast.error(
        "No labs found for provided semester or enrolment year. Change the semester or enrolment year."
      );
    } finally {
      setLabLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "current_semester" || name === "enrolment_year") {
        fetchFilteredLabs(
          name === "current_semester" ? value : prev.current_semester,
          name === "enrolment_year" ? value : prev.enrolment_year
        );
      }
      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleLabChange = (event) => {
    const selectedLabValue = event.target.value;
    const matchedLab = labOptions.find(
      (l) =>
        l.id === selectedLabValue ||
        l.lab_id === selectedLabValue ||
        (l.lab_name || l.name || l) === selectedLabValue
    );

    const lName = matchedLab ? (matchedLab.lab_name || matchedLab.name) : selectedLabValue;
    const lId = matchedLab ? (matchedLab.id || matchedLab.lab_id) : selectedLabValue;

    setFormValues((prev) => ({
      ...prev,
      lab_name: lName,
      lab_id: lId,
      ...(matchedLab?.semester && !prev.current_semester
        ? { current_semester: matchedLab.semester }
        : {}),
      ...(matchedLab?.enrolment_year && !prev.enrolment_year
        ? { enrolment_year: matchedLab.enrolment_year }
        : {}),
    }));

    if (errors.lab_name || errors.lab_id) {
      setErrors((prev) => ({ ...prev, lab_name: "", lab_id: "" }));
    }
  };

  const validateFields = () => {
    const newErrors = {};

    // Name: Required (3 to 100 characters)
    const trimmedName = formValues.name?.trim() || "";
    if (!trimmedName) {
      newErrors.name = "Name is required";
    } else if (trimmedName.length < 3 || trimmedName.length > 100) {
      newErrors.name = "Name must be between 3 and 100 characters";
    }

    // Group Name: Required
    const currentGroup = selectedGroup?.trim();
    if (!currentGroup) {
      newErrors.groupName = "Group name is required";
    }

    // Email (Personal): Required (Must be a valid email format)
    const trimmedEmail = formValues.email?.trim() || "";
    if (!trimmedEmail) {
      newErrors.email = "Personal Email (Gmail) is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      newErrors.email = "Enter a valid email address (e.g., user@gmail.com)";
    }

    // Institution Email: Required (Must be a valid email format)
    const trimmedInstEmail = formValues.institution_email?.trim() || "";
    if (!trimmedInstEmail) {
      newErrors.institution_email = "Institution email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedInstEmail)) {
      newErrors.institution_email = "Enter a valid institution email format";
    }

    // Roll Number: Required
    const trimmedRoll = formValues.roll_num?.trim() || "";
    if (!trimmedRoll) {
      newErrors.roll_num = "Roll number is required";
    }

    // Lab Name: Required
    if (!formValues.lab_name) {
      newErrors.lab_name = "Lab name is required";
    }

    // Passout Year: Required (Must be a valid year >= 1900)
    const passoutStr = formValues.passout_year?.toString().trim() || "";
    const passoutNum = parseInt(passoutStr, 10);
    if (!passoutStr) {
      newErrors.passout_year = "Passout year is required";
    } else if (!/^\d{4}$/.test(passoutStr) || isNaN(passoutNum) || passoutNum < 1900) {
      newErrors.passout_year = "Passout year must be a valid 4-digit year >= 1900";
    }

    // Enrolment Year: Required (Must be a valid year >= 1900)
    const enrolStr = formValues.enrolment_year?.toString().trim() || "";
    const enrolNum = parseInt(enrolStr, 10);
    if (!enrolStr) {
      newErrors.enrolment_year = "Enrolment year is required";
    } else if (isNaN(enrolNum) || enrolNum < 1900) {
      newErrors.enrolment_year = "Enrolment year must be a valid year >= 1900";
    }

    // Current Semester: Required (Must be between 1 and 8)
    const semStr = formValues.current_semester?.toString().trim() || "";
    const semNum = parseInt(semStr, 10);
    if (!semStr) {
      newErrors.current_semester = "Current semester is required";
    } else if (isNaN(semNum) || semNum < 1 || semNum > 8) {
      newErrors.current_semester = "Semester must be a number between 1 and 8";
    }

    // Mobile: Optional (If provided, must be a 10-digit number starting with 6, 7, 8, or 9)
    const trimmedMobile = formValues.mobile?.trim() || "";
    if (trimmedMobile && !/^[6-9]\d{9}$/.test(trimmedMobile)) {
      newErrors.mobile =
        "Mobile number must be a 10-digit number starting with 6, 7, 8, or 9";
    }

    // Device ID: Optional (Maximum 100 characters)
    const trimmedDeviceId = formValues.device_id?.trim() || "";
    if (trimmedDeviceId && trimmedDeviceId.length > 100) {
      newErrors.device_id = "Device ID cannot exceed 100 characters";
    }

    // Stream: Required (Maximum 100 characters)
    const trimmedStream = formValues.stream?.trim() || "";
    if (!trimmedStream) {
      newErrors.stream = "Stream is required";
    } else if (trimmedStream.length > 100) {
      newErrors.stream = "Stream cannot exceed 100 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;
    setLoading(true);

    const groupName = selectedGroup?.trim();
    const payload = {
      name: formValues.name.trim(),
      email: formValues.email.trim(),
      institution_email: formValues.institution_email.trim(),
      roll_num: formValues.roll_num.trim(),
      current_semester: parseInt(formValues.current_semester, 10),
      enrolment_year: parseInt(formValues.enrolment_year, 10),
      passout_year: parseInt(formValues.passout_year, 10),
      stream: formValues.stream.trim(),
      lab_name: formValues.lab_name, // Pass as is, do not trim
      mobile: formValues.mobile?.trim() || "",
    };

    if (formValues.device_id?.trim()) {
      payload.device_id = formValues.device_id.trim();
    }

    try {
      const response = await addSingleLearner(coeId, groupName, payload);
      toast.success(
        response?.data?.message || "Learner added successfully!"
      );
      if (fetchData) fetchData();
      if (fetchCoeData) fetchCoeData();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        error?.error ||
        "Failed to add learner."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2.5 }}>
          Add Individual Learner
        </Typography>

        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Name */}
          <TextField
            size="small"
            label="Name"
            name="name"
            value={formValues.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            required
            disabled={loading}
            fullWidth
            inputProps={{ minLength: 3, maxLength: 100 }}
          />

          {/* Group Name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!isCustomGroup ? (
              <Autocomplete
                options={groupOptions}
                sx={{ width: "100%" }}
                value={selectedGroup}
                onChange={(event, newValue) => {
                  setSelectedGroup(newValue || "");
                  if (errors.groupName) {
                    setErrors((prev) => ({ ...prev, groupName: "" }));
                  }
                }}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Select Group Name"
                    error={!!errors.groupName}
                    helperText={errors.groupName}
                    required
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                value={selectedGroup}
                onChange={(e) => {
                  setSelectedGroup(e.target.value);
                  if (errors.groupName) {
                    setErrors((prev) => ({ ...prev, groupName: "" }));
                  }
                }}
                label="Enter Group Name"
                error={!!errors.groupName}
                helperText={errors.groupName}
                required
                disabled={loading}
              />
            )}
            <IconButton
              onClick={() => setIsCustomGroup(!isCustomGroup)}
              disabled={loading}
              title={
                isCustomGroup ? "Select existing group" : "Enter new group"
              }
            >
              {isCustomGroup ? (
                <RemoveIcon color="error" />
              ) : (
                <AddIcon color="primary" />
              )}
            </IconButton>
          </Box>

               {/* Lab Name Select */}
          <FormControl fullWidth size="small" error={!!errors.lab_name || !!errors.lab_id} required>
            <InputLabel id="add-student-lab-label">Lab Name</InputLabel>
            <Select
              labelId="add-student-lab-label"
              id="add-student-lab-select"
              name="lab_id"
              value={formValues.lab_id || formValues.lab_name}
              onChange={handleLabChange}
              label="Lab Name"
              disabled={loading}
            >
              {labOptions.length > 0 ? (
                labOptions.map((lab, index) => {
                  const labName =
                    typeof lab === "string"
                      ? lab
                      : lab.lab_name || lab.name || "";
                  const labId = typeof lab === "object" ? (lab.id || lab.lab_id || labName) : labName;
                  return (
                    <MenuItem key={labId || index} value={labId}>
                      {labName}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem value="" disabled>
                  No labs found for this COE
                </MenuItem>
              )}
            </Select>
            {(errors.lab_name || errors.lab_id) && (
              <FormHelperText>{errors.lab_name || errors.lab_id}</FormHelperText>
            )}
          </FormControl>

          {/* Institution Email */}
          <TextField
            size="small"
            label="Institution Email"
            name="institution_email"
            type="email"
            value={formValues.institution_email}
            onChange={handleChange}
            error={!!errors.institution_email}
            helperText={errors.institution_email}
            required
            disabled={loading}
            fullWidth
          />
           {/* Personal Gmail */}
          <TextField
            size="small"
            label="Personal Gmail"
            name="email"
            type="email"
            value={formValues.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            required
            disabled={loading}
            fullWidth
          />

          {/* Roll Number */}
          <TextField
            size="small"
            label="Roll Number"
            name="roll_num"
            value={formValues.roll_num}
            onChange={handleChange}
            error={!!errors.roll_num}
            helperText={errors.roll_num}
            required
            disabled={loading}
            fullWidth
          />

          {/* Current Semester (1 to 8) Select */}
          <FormControl fullWidth size="small" error={!!errors.current_semester} required>
            <InputLabel id="add-student-semester-label">Current Semester</InputLabel>
            <Select
              labelId="add-student-semester-label"
              id="add-student-semester-select"
              name="current_semester"
              value={formValues.current_semester}
              onChange={handleChange}
              label="Current Semester"
              disabled={loading}
            >
              {semesterOptions.map((sem) => (
                <MenuItem key={sem} value={sem}>
                  Semester {sem}
                </MenuItem>
              ))}
            </Select>
            {errors.current_semester && (
              <FormHelperText>{errors.current_semester}</FormHelperText>
            )}
          </FormControl>

          {/* Enrolment Year Select */}
          <FormControl fullWidth size="small" error={!!errors.enrolment_year} required>
            <InputLabel id="add-student-enrolment-year-label">Enrolment Year</InputLabel>
            <Select
              labelId="add-student-enrolment-year-label"
              id="add-student-enrolment-year-select"
              name="enrolment_year"
              value={formValues.enrolment_year}
              onChange={handleChange}
              label="Enrolment Year"
              disabled={loading}
            >
              {(enrolmentYearOptions.length > 0
                ? enrolmentYearOptions
                : [2026, 2025, 2024, 2023, 2022, 2021, 2020]
              ).map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
            {errors.enrolment_year && (
              <FormHelperText>{errors.enrolment_year}</FormHelperText>
            )}
          </FormControl>

    

          {/* Passout Year (>= 1900) */}
          <TextField
            size="small"
            label="Passout Year"
            name="passout_year"
            type="number"
            value={formValues.passout_year}
            onChange={handleChange}
            error={!!errors.passout_year}
            helperText={errors.passout_year}
            required
            disabled={loading}
            fullWidth
            inputProps={{ min: 1900 }}
          />
          {/* Stream */}
          <TextField
            size="small"
            label="Stream"
            name="stream"
            value={formValues.stream}
            onChange={handleChange}
            error={!!errors.stream}
            helperText={errors.stream}
            required
            disabled={loading}
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
          {/* Mobile (Optional, 10 digits starting with 6,7,8,9) */}
          <TextField
            size="small"
            label="Mobile Number (Optional)"
            name="mobile"
            value={formValues.mobile}
            onChange={handleChange}
            error={!!errors.mobile}
            helperText={errors.mobile || "10-digit number starting with 6, 7, 8, or 9"}
            disabled={loading}
            fullWidth
            inputProps={{ maxLength: 10 }}
          />

          {/* Device ID (Optional, max 100 characters) */}
          <TextField
            size="small"
            label="Device ID (Optional)"
            name="device_id"
            value={formValues.device_id}
            onChange={handleChange}
            error={!!errors.device_id}
            helperText={errors.device_id}
            disabled={loading}
            fullWidth
            inputProps={{ maxLength: 100 }}
          />



          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              mt: 1,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ textTransform: "capitalize", px: 3 }}
            >
              {loading ? "Saving..." : "Add Learner"}
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{ textTransform: "capitalize", px: 3 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddStudentModal;



